import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Eye, Sparkles, LayoutGrid, Music } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const CanvasItemView = ({ item }) => {
  const content = resolveUrl(item.content);
  const style = {
    position: 'absolute',
    left: item.x,
    top: item.y,
    width: item.width,
    height: item.height,
  };
  if (item.type === 'image') {
    return (
      <div style={style} className="rounded-lg overflow-hidden border border-white/10 shadow-lg">
        <img src={content} alt={item.label || 'Canvas item'} className="w-full h-full object-cover" loading="lazy" />
      </div>
    );
  }
  if (item.type === 'video') {
    return (
      <div style={style} className="rounded-lg overflow-hidden border border-white/10 shadow-lg bg-black">
        <video src={content} controls playsInline className="w-full h-full object-contain" preload="metadata" />
      </div>
    );
  }
  if (item.type === 'link') {
    return (
      <a href={content} target="_blank" rel="noopener noreferrer" style={style}
        className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-3 text-xs text-orange-300 break-all hover:bg-orange-500/20 transition-colors flex items-center">
        {item.label || item.content}
      </a>
    );
  }
  // note / text
  return (
    <div style={{ ...style, backgroundColor: item.color || '#1a1a25' }}
      className="rounded-lg border border-white/10 p-3 text-sm text-white/90 overflow-hidden shadow-lg">
      {item.content || item.label}
    </div>
  );
};

export default function SharePage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Unlisted-style privacy: keep share pages out of search engines
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);
    axios.get(`${BACKEND_URL}/api/shares/${shareId}/public`, { timeout: 20000 })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This share link is unavailable'));
    return () => { document.head.removeChild(meta); };
  }, [shareId]);

  const canvasBounds = data?.kind === 'canvas'
    ? (data.items || []).reduce(
        (acc, i) => ({
          w: Math.max(acc.w, (i.x || 0) + (i.width || 200) + 40),
          h: Math.max(acc.h, (i.y || 0) + (i.height || 200) + 40),
        }),
        { w: 800, h: 600 }
      )
    : null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white flex flex-col" data-testid="share-page">
      {/* Header */}
      <header className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="share-logo-link">
            <Sparkles className="w-5 h-5 text-orange-400" />
            <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Delphi <span className="text-orange-400">AI</span>
            </span>
          </Link>
          <Link
            to="/auth"
            data-testid="share-cta-header"
            className="px-4 py-2 rounded-full text-sm font-semibold bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            Create your own
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-6 py-10">
        {error ? (
          <div className="h-[50vh] flex flex-col items-center justify-center text-center" data-testid="share-error">
            <Sparkles className="w-10 h-10 text-white/20 mb-4" />
            <h1 className="text-xl font-semibold text-white/80 mb-2">Link unavailable</h1>
            <p className="text-sm text-white/40 mb-6">{error}</p>
            <Link to="/" className="text-orange-400 hover:text-orange-300 text-sm">← Back to Delphi AI</Link>
          </div>
        ) : !data ? (
          <div className="h-[50vh] flex items-center justify-center" data-testid="share-loading">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : (
          <div>
            {/* Title block */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-xs text-white/40 mb-2">
                {data.kind === 'canvas' ? <LayoutGrid className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
                <span>Shared by <span className="text-orange-400 font-medium">{data.creator}</span></span>
                <span>·</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{data.views} views</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="share-title">
                {data.kind === 'canvas' ? data.name : data.title}
              </h1>
              {data.kind === 'canvas' && data.description && (
                <p className="text-sm text-white/40 mt-1">{data.description}</p>
              )}
            </div>

            {/* Content */}
            {data.kind === 'media' ? (
              <div className="rounded-2xl border border-white/[0.08] bg-[#0d0d14] overflow-hidden" data-testid="share-media">
                {data.media_type === 'image' && (
                  <img src={resolveUrl(data.url)} alt={data.title} className="w-full max-h-[75vh] object-contain bg-black" />
                )}
                {data.media_type === 'video' && (
                  <video
                    src={resolveUrl(data.url)}
                    poster={resolveUrl(data.thumbnail_url) || undefined}
                    controls
                    playsInline
                    className="w-full max-h-[75vh] bg-black"
                    data-testid="share-video"
                  />
                )}
                {data.media_type === 'audio' && (
                  <div className="p-10 flex flex-col items-center gap-6">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-500/30 to-red-600/30 border border-orange-500/30 flex items-center justify-center">
                      <Music className="w-10 h-10 text-orange-400" />
                    </div>
                    <audio src={resolveUrl(data.url)} controls className="w-full max-w-md" data-testid="share-audio" />
                  </div>
                )}
                {data.prompt && (
                  <div className="p-4 border-t border-white/[0.06]">
                    <p className="text-xs text-white/30 uppercase tracking-wider mb-1">Prompt</p>
                    <p className="text-sm text-white/60">{data.prompt}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-white/[0.08] overflow-auto" data-testid="share-canvas">
                <div
                  className="relative bg-[radial-gradient(#1a1a25_1px,transparent_1px)] [background-size:24px_24px]"
                  style={{ backgroundColor: data.background || '#0a0a0f', width: canvasBounds.w, height: canvasBounds.h, minWidth: '100%' }}
                >
                  {(data.items || []).map((item) => <CanvasItemView key={item.id} item={item} />)}
                  {(data.items || []).length === 0 && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/30 text-sm">This board is empty</div>
                  )}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="mt-10 rounded-2xl border border-orange-500/20 bg-gradient-to-r from-orange-500/[0.08] to-red-600/[0.08] p-8 text-center" data-testid="share-cta-block">
              <h2 className="text-lg font-semibold mb-2">Made with Delphi AI</h2>
              <p className="text-sm text-white/50 mb-5 max-w-md mx-auto">
                Generate cinematic images, videos, music and full campaigns with The Oracle — your AI creative supercomputer.
              </p>
              <Link
                to="/auth"
                data-testid="share-cta-footer"
                className="inline-block px-6 py-3 rounded-full font-semibold bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 transition-colors"
              >
                Start creating free
              </Link>
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-white/[0.06] py-6 text-center text-xs text-white/30">
        <Link to="/" className="hover:text-white/60 transition-colors">Delphi AI</Link> · Prophesy your next viral moment
      </footer>
    </div>
  );
}
